import React, { useEffect, useState } from "react";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Badge } from "../../components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../../components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import {
  Database, Server, Activity, RefreshCw, Trash2,
  CheckCircle, AlertTriangle, Download, Wrench, ArrowRight, Eye, Play, ShieldCheck, Search,
  KeySquare, Phone, Cloud,
} from "lucide-react";
import { API } from "../../App";
import { toast } from "sonner";
import { format } from "date-fns";

import ServerInfoPanel from "./system/ServerInfoPanel";
import DatabaseInfoPanel from "./system/DatabaseInfoPanel";
import EnvironmentPanel from "./system/EnvironmentPanel";
import TwilioDiagnosticsPanel from "./system/TwilioDiagnosticsPanel";
import StoragePanel from "./system/StoragePanel";

// Auto-refresh server snapshot every 10 seconds when on the "Server" tab.
const SERVER_AUTO_REFRESH_MS = 10000;

const AdminSystem = () => {
  const [loading, setLoading] = useState(true);
  const [systemInfo, setSystemInfo] = useState(null);
  const [dbStats, setDbStats] = useState(null);
  const [healthChecks, setHealthChecks] = useState([]);
  const [activeTab, setActiveTab] = useState("server");
  const [refreshing, setRefreshing] = useState(false);
  const [lastRefreshed, setLastRefreshed] = useState(null);

  // Fix Old Jobs state
  const [users, setUsers] = useState([]);
  const [selectedUserId, setSelectedUserId] = useState("");
  const [fixResult, setFixResult] = useState(null);
  const [fixLoading, setFixLoading] = useState(false);

  // Data Integrity Scanner state
  const [scanResult, setScanResult] = useState(null);
  const [scanLoading, setScanLoading] = useState(false);

  // A2P Phone Numbers state
  const [phoneNumbers, setPhoneNumbers] = useState([]);
  const [phonesLoading, setPhonesLoading] = useState(false);
  const [linkingId, setLinkingId] = useState(null);
  const [bulkLinking, setBulkLinking] = useState(false);

  const getAuthConfig = () => {
    const token = localStorage.getItem("token");
    return { headers: token ? { Authorization: `Bearer ${token}` } : {} };
  };

  const fetchData = async (silent = false) => {
    if (!silent) setLoading(true);
    setRefreshing(true);
    try {
      const [sysRes, dbRes, healthRes, usersRes] = await Promise.all([
        axios.get(`${API}/admin/system-info`, getAuthConfig()),
        axios.get(`${API}/admin/database-stats`, getAuthConfig()),
        axios.get(`${API}/admin/health-checks`, getAuthConfig()),
        axios.get(`${API}/admin/users-list`, getAuthConfig()),
      ]);
      setSystemInfo(sysRes.data);
      setDbStats(dbRes.data);
      setHealthChecks(healthRes.data.checks || []);
      const usersData = usersRes.data;
      setUsers(Array.isArray(usersData) ? usersData : usersData.users || []);
      setLastRefreshed(new Date());
    } catch (error) {
      if (!silent) toast.error("Failed to load system data");
      // eslint-disable-next-line no-console
      console.error("System data fetch failed:", error);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Auto-refresh while on server / database tabs so live counters keep updating.
  useEffect(() => {
    if (!["server", "database"].includes(activeTab)) return undefined;
    const id = setInterval(() => fetchData(true), SERVER_AUTO_REFRESH_MS);
    return () => clearInterval(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab]);

  // ── A2P Phone Number Management ──
  const fetchPhoneNumbers = async () => {
    setPhonesLoading(true);
    try {
      const res = await axios.get(`${API}/admin/a2p/phone-numbers`, getAuthConfig());
      setPhoneNumbers(res.data || []);
    } catch (e) {
      toast.error("Failed to load phone numbers");
    } finally {
      setPhonesLoading(false);
    }
  };

  const linkSingleNumber = async (numberId) => {
    setLinkingId(numberId);
    try {
      const res = await axios.post(`${API}/admin/a2p/link-number`, { number_id: numberId }, getAuthConfig());
      toast.success(res.data.message);
      fetchPhoneNumbers();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to link number");
    } finally {
      setLinkingId(null);
    }
  };

  const linkAllNumbers = async () => {
    setBulkLinking(true);
    try {
      const res = await axios.post(`${API}/admin/a2p/link-all-numbers`, {}, getAuthConfig());
      toast.success(res.data.message);
      if (res.data.results?.failed?.length > 0) {
        toast.error(`${res.data.results.failed.length} numbers failed to link`);
      }
      fetchPhoneNumbers();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Bulk linking failed");
    } finally {
      setBulkLinking(false);
    }
  };

  const runFixOldJobs = async (dryRun) => {
    if (!selectedUserId) {
      toast.error("Select a user account first");
      return;
    }
    setFixLoading(true);
    setFixResult(null);
    try {
      const res = await axios.post(`${API}/admin/fix-old-jobs`, {
        user_id: selectedUserId,
        dry_run: dryRun,
      }, getAuthConfig());
      setFixResult(res.data);
      if (dryRun) {
        toast.success(`Preview: ${res.data.jobs_with_changes} jobs would change`);
      } else {
        toast.success(`Fixed ${res.data.jobs_with_changes} jobs successfully`);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to fix old jobs");
    } finally {
      setFixLoading(false);
    }
  };

  const runDataScan = async (dryRun) => {
    setScanLoading(true);
    setScanResult(null);
    try {
      const res = await axios.post(`${API}/admin/scan-fix-data`, { dry_run: dryRun }, getAuthConfig());
      setScanResult(res.data);
      if (dryRun) {
        toast.success(`Scan complete: ${res.data.issues_found} issues found across ${res.data.total_users_scanned} users`);
      } else {
        toast.success(`Fixed ${res.data.issues_fixed} issues successfully`);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Scan failed");
    } finally {
      setScanLoading(false);
    }
  };

  const clearCache = async () => {
    try {
      await axios.post(`${API}/admin/clear-cache`, {}, getAuthConfig());
      toast.success("Cache cleared successfully");
    } catch {
      toast.error("Failed to clear cache");
    }
  };

  const runHealthCheck = async () => {
    try {
      const res = await axios.post(`${API}/admin/run-health-check`, {}, getAuthConfig());
      setHealthChecks(res.data.checks || []);
      toast.success("Health check completed");
    } catch {
      toast.error("Health check failed");
    }
  };

  const exportLogs = async () => {
    try {
      const res = await axios.get(`${API}/admin/export-logs`, {
        ...getAuthConfig(),
        responseType: "blob",
      });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `system_logs_${format(new Date(), "yyyy-MM-dd")}.txt`;
      a.click();
      toast.success("Logs exported");
    } catch {
      toast.error("Failed to export logs");
    }
  };

  // Health summary for the header strip
  const healthSummary = healthChecks.reduce(
    (acc, c) => {
      if (c.status === "healthy") acc.healthy += 1;
      else if (c.status === "warning") acc.warning += 1;
      else acc.error += 1;
      return acc;
    },
    { healthy: 0, warning: 0, error: 0 }
  );
  const overallStatus = healthSummary.error > 0 ? "error" : healthSummary.warning > 0 ? "warning" : "healthy";

  return (
    <div className="space-y-6" data-testid="admin-system-page">
      {/* Page Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold text-foreground font-['Manrope']" data-testid="system-page-title">System &amp; Database</h1>
          <p className="text-muted-foreground text-sm">
            Live OS, hardware, runtime &amp; database telemetry — refreshes every {SERVER_AUTO_REFRESH_MS / 1000}s on the active tab.
          </p>
        </div>
        <div className="flex flex-wrap gap-2 items-center">
          {lastRefreshed && (
            <span className="text-[10px] text-muted-foreground" data-testid="last-refreshed">
              Last refresh: {format(lastRefreshed, "HH:mm:ss")}
            </span>
          )}
          <Button variant="outline" size="sm" onClick={clearCache} className="border-theme text-foreground" data-testid="clear-cache-btn">
            <Trash2 className="w-4 h-4 mr-1.5" /> Clear Cache
          </Button>
          <Button variant="outline" size="sm" onClick={exportLogs} className="border-theme text-foreground" data-testid="export-logs-btn">
            <Download className="w-4 h-4 mr-1.5" /> Export Logs
          </Button>
          <Button onClick={() => fetchData(false)} disabled={refreshing} size="sm" className="bg-[#0046BE] text-white" data-testid="refresh-system-btn">
            <RefreshCw className={`w-4 h-4 mr-1.5 ${refreshing ? "animate-spin" : ""}`} /> Refresh
          </Button>
        </div>
      </div>

      {/* Health Summary Strip */}
      <Card className="glass-card border-theme" data-testid="health-summary-strip">
        <CardContent className="p-4 flex flex-col md:flex-row md:items-center gap-3 md:gap-6">
          <div className="flex items-center gap-3">
            <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
              overallStatus === "healthy" ? "bg-emerald-500/20" :
              overallStatus === "warning" ? "bg-amber-500/20" : "bg-red-500/20"
            }`}>
              {overallStatus === "healthy"
                ? <CheckCircle className="w-5 h-5 text-emerald-500" />
                : <AlertTriangle className={`w-5 h-5 ${overallStatus === "warning" ? "text-amber-500" : "text-red-500"}`} />}
            </div>
            <div>
              <p className="text-base font-bold text-foreground font-['Manrope']">
                {overallStatus === "healthy" ? "All systems operational" :
                 overallStatus === "warning" ? "Operational with warnings" : "Issues detected"}
              </p>
              <p className="text-xs text-muted-foreground">
                {healthChecks.length === 0
                  ? "Run a health check to see live probe results"
                  : `${healthSummary.healthy} healthy · ${healthSummary.warning} warning · ${healthSummary.error} error`}
              </p>
            </div>
          </div>

          <div className="flex-1 grid grid-cols-2 md:grid-cols-4 gap-2">
            {healthChecks.slice(0, 4).map((c, i) => (
              <div key={i} className="bg-muted/40 rounded-lg p-2.5" data-testid={`health-pill-${i}`}>
                <div className="flex items-center gap-1.5 mb-0.5">
                  {c.status === "healthy"
                    ? <CheckCircle className="w-3 h-3 text-emerald-500" />
                    : c.status === "warning"
                      ? <AlertTriangle className="w-3 h-3 text-amber-500" />
                      : <AlertTriangle className="w-3 h-3 text-red-500" />}
                  <span className="text-[11px] font-semibold text-foreground truncate">{c.name}</span>
                </div>
                <p className="text-[10px] text-muted-foreground truncate" title={c.message}>{c.message}</p>
              </div>
            ))}
          </div>

          <Button variant="outline" size="sm" onClick={runHealthCheck} className="border-theme text-foreground" data-testid="run-health-check-btn">
            <Activity className="w-4 h-4 mr-1.5" /> Run Health Check
          </Button>
        </CardContent>
      </Card>

      {/* Tabbed Body */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-4">
        <TabsList className="grid grid-cols-4 sm:grid-cols-7 w-full md:w-auto md:inline-flex" data-testid="system-tabs">
          <TabsTrigger value="server" data-testid="tab-server"><Server className="w-3.5 h-3.5 mr-1.5" />Server</TabsTrigger>
          <TabsTrigger value="database" data-testid="tab-database"><Database className="w-3.5 h-3.5 mr-1.5" />Database</TabsTrigger>
          <TabsTrigger value="storage" data-testid="tab-storage"><Cloud className="w-3.5 h-3.5 mr-1.5" />Storage</TabsTrigger>
          <TabsTrigger value="environment" data-testid="tab-environment"><KeySquare className="w-3.5 h-3.5 mr-1.5" />Environment</TabsTrigger>
          <TabsTrigger value="twilio" data-testid="tab-twilio"><Phone className="w-3.5 h-3.5 mr-1.5" />Twilio</TabsTrigger>
          <TabsTrigger value="health" data-testid="tab-health"><Activity className="w-3.5 h-3.5 mr-1.5" />Health</TabsTrigger>
          <TabsTrigger value="tools" data-testid="tab-tools"><Wrench className="w-3.5 h-3.5 mr-1.5" />Tools</TabsTrigger>
        </TabsList>

        {/* SERVER TAB */}
        <TabsContent value="server" className="space-y-4">
          {loading ? (
            <Card className="glass-card border-theme">
              <CardContent className="p-12 text-center text-muted-foreground text-sm">Loading server snapshot…</CardContent>
            </Card>
          ) : (
            <ServerInfoPanel systemInfo={systemInfo} />
          )}
        </TabsContent>

        {/* DATABASE TAB */}
        <TabsContent value="database" className="space-y-4">
          {loading ? (
            <Card className="glass-card border-theme">
              <CardContent className="p-12 text-center text-muted-foreground text-sm">Loading database snapshot…</CardContent>
            </Card>
          ) : (
            <DatabaseInfoPanel dbStats={dbStats} />
          )}
        </TabsContent>

        {/* STORAGE TAB — Emergent Object Storage backend + usage + migration */}
        <TabsContent value="storage" className="space-y-4">
          <StoragePanel />
        </TabsContent>

        {/* ENVIRONMENT TAB */}
        <TabsContent value="environment" className="space-y-4">
          <EnvironmentPanel envVars={systemInfo?.environment_variables || []} />
        </TabsContent>

        {/* TWILIO TAB — voice integration health, in-house failures, live Twilio Monitor alerts */}
        <TabsContent value="twilio" className="space-y-4">
          <TwilioDiagnosticsPanel />
        </TabsContent>

        {/* HEALTH TAB */}
        <TabsContent value="health" className="space-y-4">
          <Card className="glass-card border-theme" data-testid="health-detail-card">
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle className="text-foreground flex items-center gap-2">
                  <Activity className="w-5 h-5" /> Live Health Probes
                </CardTitle>
                <Button variant="outline" size="sm" onClick={runHealthCheck} className="border-theme text-foreground" data-testid="run-health-detail-btn">
                  <RefreshCw className="w-4 h-4 mr-1.5" /> Re-run
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              <div className="space-y-2">
                {healthChecks.length === 0 ? (
                  <p className="text-center text-muted-foreground py-8 text-sm">No health data yet — click Re-run.</p>
                ) : healthChecks.map((check, i) => (
                  <div key={i} className="flex items-center justify-between bg-muted/40 rounded-lg p-3" data-testid={`health-detail-${i}`}>
                    <div className="flex items-center gap-3">
                      {check.status === "healthy"
                        ? <CheckCircle className="w-5 h-5 text-emerald-500" />
                        : check.status === "warning"
                          ? <AlertTriangle className="w-5 h-5 text-amber-500" />
                          : <AlertTriangle className="w-5 h-5 text-red-500" />}
                      <div>
                        <p className="text-foreground text-sm font-medium">{check.name}</p>
                        <p className="text-muted-foreground text-xs">{check.message || "—"}</p>
                      </div>
                    </div>
                    <Badge className={
                      check.status === "healthy" ? "bg-emerald-500/20 text-emerald-500 border-emerald-500/30" :
                      check.status === "warning" ? "bg-amber-500/20 text-amber-500 border-amber-500/30" :
                      "bg-red-500/20 text-red-500 border-red-500/30"
                    } variant="outline">
                      {check.status}
                    </Badge>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* TOOLS TAB — preserved admin tools */}
        <TabsContent value="tools" className="space-y-4">
          {/* Data Integrity Scanner */}
          <Card className="glass-card border-theme" data-testid="data-integrity-scanner-card">
            <CardHeader>
              <CardTitle className="text-foreground font-['Manrope'] flex items-center gap-2">
                <ShieldCheck className="w-5 h-5 text-blue-500" />
                Data Integrity Scanner
              </CardTitle>
              <CardDescription className="text-muted-foreground">
                Scan all accounts for missing source commissions on old jobs and fix them automatically
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex flex-col sm:flex-row gap-3" data-testid="scan-controls">
                <Button variant="outline" onClick={() => runDataScan(true)} disabled={scanLoading} className="border-theme text-foreground" data-testid="scan-preview-btn">
                  <Search className="w-4 h-4 mr-2" /> {scanLoading ? "Scanning..." : "Scan for Problems"}
                </Button>
                <Button onClick={() => runDataScan(false)} disabled={scanLoading || !scanResult || scanResult.issues_found === 0} className="bg-blue-600 hover:bg-blue-700 text-white" data-testid="scan-fix-btn">
                  <ShieldCheck className="w-4 h-4 mr-2" /> {scanLoading ? "Fixing..." : "Fix All Issues"}
                </Button>
              </div>

              {scanResult && (
                <div className="space-y-3 mt-4" data-testid="scan-result">
                  <div className="flex flex-wrap gap-3">
                    <Badge variant="outline" className="text-foreground border-theme px-3 py-1">
                      {scanResult.dry_run ? "SCAN PREVIEW" : "FIXES APPLIED"}
                    </Badge>
                    <Badge variant="outline" className="border-theme px-3 py-1 text-muted-foreground">
                      {scanResult.total_users_scanned} users scanned
                    </Badge>
                    <Badge variant="outline" className="border-theme px-3 py-1 text-muted-foreground">
                      {scanResult.total_jobs_scanned} jobs checked
                    </Badge>
                    <Badge className={`px-3 py-1 ${scanResult.issues_found > 0 ? "bg-orange-500/15 text-orange-400 border-orange-500/20" : "bg-emerald-500/15 text-emerald-400 border-emerald-500/20"}`}>
                      {scanResult.issues_found === 0 ? "No issues found" : `${scanResult.issues_found} issues ${scanResult.dry_run ? "found" : "fixed"}`}
                    </Badge>
                  </div>

                  {scanResult.issues_found > 0 && (
                    <div className="max-h-[400px] overflow-y-auto rounded-lg border border-theme">
                      <table className="w-full text-sm">
                        <thead className="bg-theme-surface sticky top-0">
                          <tr className="text-muted-foreground text-left text-xs">
                            <th className="p-2">Account</th>
                            <th className="p-2">Job</th>
                            <th className="p-2">Source</th>
                            <th className="p-2">Revenue</th>
                            <th className="p-2">Old Commission</th>
                            <th className="p-2">New Commission</th>
                            <th className="p-2">Profit Diff</th>
                          </tr>
                        </thead>
                        <tbody>
                          {scanResult.issues.map((issue, i) => (
                            <tr key={i} className="border-t border-theme/50 text-foreground">
                              <td className="p-2"><div className="text-xs font-medium">{issue.user_name || issue.user_email}</div></td>
                              <td className="p-2">
                                <div className="text-xs">{issue.customer}</div>
                                <div className="text-[10px] text-muted-foreground">{issue.job_display_id}</div>
                              </td>
                              <td className="p-2 text-xs">{issue.source_name}</td>
                              <td className="p-2">${issue.revenue}</td>
                              <td className="p-2 text-muted-foreground">${issue.old_source_commission}</td>
                              <td className="p-2 font-medium">${issue.new_source_commission}</td>
                              <td className={`p-2 font-medium ${issue.profit_diff >= 0 ? "text-emerald-400" : "text-red-400"}`}>
                                {issue.profit_diff >= 0 ? "+" : ""}{issue.profit_diff}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Fix Old Jobs */}
          <Card className="glass-card border-theme" data-testid="fix-old-jobs-card">
            <CardHeader>
              <CardTitle className="text-foreground font-['Manrope'] flex items-center gap-2">
                <Wrench className="w-5 h-5 text-orange-500" />
                Fix Old Jobs — Recalculate Financials
              </CardTitle>
              <CardDescription className="text-muted-foreground">
                Recalculate parts cost, commissions, processing fees, and net profit for all completed jobs of a specific user account
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex flex-col sm:flex-row gap-3" data-testid="fix-jobs-controls">
                <Select value={selectedUserId} onValueChange={setSelectedUserId}>
                  <SelectTrigger className="input-glass sm:w-[320px]" data-testid="fix-jobs-user-select">
                    <SelectValue placeholder="Select user account..." />
                  </SelectTrigger>
                  <SelectContent>
                    {users.map(u => (
                      <SelectItem key={u.user_id} value={u.user_id}>
                        {u.name || u.email} {u.company_name ? `(${u.company_name})` : ""}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Button variant="outline" onClick={() => runFixOldJobs(true)} disabled={fixLoading || !selectedUserId} className="border-theme text-foreground" data-testid="fix-jobs-preview-btn">
                  <Eye className="w-4 h-4 mr-2" /> {fixLoading ? "Scanning..." : "Preview Changes"}
                </Button>
                <Button onClick={() => runFixOldJobs(false)} disabled={fixLoading || !selectedUserId} className="bg-orange-500 hover:bg-orange-600 text-white" data-testid="fix-jobs-apply-btn">
                  <Play className="w-4 h-4 mr-2" /> {fixLoading ? "Fixing..." : "Apply Fixes"}
                </Button>
              </div>

              {fixResult && (
                <div className="space-y-3 mt-4" data-testid="fix-jobs-result">
                  <div className="flex flex-wrap gap-3">
                    <Badge variant="outline" className="text-foreground border-theme px-3 py-1">{fixResult.dry_run ? "DRY RUN" : "APPLIED"}</Badge>
                    <Badge variant="outline" className="border-theme px-3 py-1 text-muted-foreground">User: {fixResult.user?.name || fixResult.user?.email}</Badge>
                    <Badge variant="outline" className="border-theme px-3 py-1 text-muted-foreground">Scanned: {fixResult.total_jobs_scanned} jobs</Badge>
                    <Badge className={`px-3 py-1 ${fixResult.jobs_with_changes > 0 ? "bg-orange-500/15 text-orange-400 border-orange-500/20" : "bg-emerald-500/15 text-emerald-400 border-emerald-500/20"}`}>
                      {fixResult.jobs_with_changes} jobs {fixResult.dry_run ? "need changes" : "fixed"}
                    </Badge>
                  </div>

                  {fixResult.jobs_with_changes > 0 && (
                    <>
                      <div className="grid grid-cols-3 gap-3">
                        <div className="p-3 rounded-lg bg-theme-surface border border-theme text-center">
                          <p className="text-xs text-muted-foreground">Old Total Profit</p>
                          <p className="text-lg font-bold text-foreground">${fixResult.total_old_profit?.toLocaleString()}</p>
                        </div>
                        <div className="p-3 rounded-lg bg-theme-surface border border-theme text-center flex flex-col items-center justify-center">
                          <ArrowRight className="w-5 h-5 text-muted-foreground" />
                        </div>
                        <div className="p-3 rounded-lg bg-theme-surface border border-theme text-center">
                          <p className="text-xs text-muted-foreground">New Total Profit</p>
                          <p className="text-lg font-bold text-foreground">${fixResult.total_new_profit?.toLocaleString()}</p>
                          <p className={`text-xs font-medium ${fixResult.profit_difference >= 0 ? "text-emerald-400" : "text-red-400"}`}>
                            {fixResult.profit_difference >= 0 ? "+" : ""}{fixResult.profit_difference?.toLocaleString()}
                          </p>
                        </div>
                      </div>

                      <div className="max-h-[400px] overflow-y-auto rounded-lg border border-theme">
                        <table className="w-full text-sm">
                          <thead className="bg-theme-surface sticky top-0">
                            <tr className="text-muted-foreground text-left text-xs">
                              <th className="p-2">Job</th>
                              <th className="p-2">Revenue</th>
                              <th className="p-2">Old Profit</th>
                              <th className="p-2">New Profit</th>
                              <th className="p-2">Diff</th>
                            </tr>
                          </thead>
                          <tbody>
                            {fixResult.changes.map((c) => (
                              <tr key={c.job_id} className="border-t border-theme/50 text-foreground">
                                <td className="p-2">
                                  <div className="text-xs font-medium">{c.customer}</div>
                                  <div className="text-[10px] text-muted-foreground">{c.job_display_id || c.job_id}</div>
                                </td>
                                <td className="p-2">${c.revenue}</td>
                                <td className="p-2 text-muted-foreground">${c.old.net_profit}</td>
                                <td className="p-2 font-medium">${c.new.net_profit}</td>
                                <td className={`p-2 font-medium ${c.profit_diff >= 0 ? "text-emerald-400" : "text-red-400"}`}>
                                  {c.profit_diff >= 0 ? "+" : ""}{c.profit_diff}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </>
                  )}
                </div>
              )}
            </CardContent>
          </Card>

          {/* A2P Phone Number Management */}
          <Card className="glass-card border-theme" data-testid="a2p-management-card">
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="text-lg flex items-center gap-2"><ShieldCheck className="w-5 h-5 text-emerald-500" />A2P 10DLC Phone Management</CardTitle>
                  <CardDescription>Link phone numbers to your approved A2P messaging service for SMS compliance</CardDescription>
                </div>
                <Button variant="outline" size="sm" onClick={fetchPhoneNumbers} disabled={phonesLoading} data-testid="load-phones-btn">
                  <RefreshCw className={`w-4 h-4 ${phonesLoading ? "animate-spin" : ""}`} />
                  <span className="ml-1.5">Load Numbers</span>
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              {phoneNumbers.length === 0 ? (
                <p className="text-muted-foreground text-sm">Click "Load Numbers" to fetch all phone numbers and their A2P status.</p>
              ) : (
                <>
                  <div className="flex items-center justify-between mb-4 p-3 bg-muted/40 rounded-lg">
                    <div className="flex gap-4 text-sm">
                      <span><strong>{phoneNumbers.length}</strong> total numbers</span>
                      <span className="text-emerald-500"><strong>{phoneNumbers.filter(n => n.a2p_linked).length}</strong> linked</span>
                      <span className="text-amber-500"><strong>{phoneNumbers.filter(n => !n.a2p_linked).length}</strong> unlinked</span>
                    </div>
                    {phoneNumbers.some(n => !n.a2p_linked) && (
                      <Button size="sm" onClick={linkAllNumbers} disabled={bulkLinking} data-testid="link-all-btn">
                        <ShieldCheck className={`w-4 h-4 mr-1.5 ${bulkLinking ? "animate-spin" : ""}`} />
                        Link All Unlinked Numbers
                      </Button>
                    )}
                  </div>

                  <div className="border rounded-lg overflow-auto max-h-96">
                    <table className="w-full text-sm">
                      <thead className="bg-muted/60 sticky top-0">
                        <tr>
                          <th className="text-left px-3 py-2 font-medium">Phone Number</th>
                          <th className="text-left px-3 py-2 font-medium">Owner</th>
                          <th className="text-left px-3 py-2 font-medium">Label</th>
                          <th className="text-center px-3 py-2 font-medium">A2P Status</th>
                          <th className="text-right px-3 py-2 font-medium">Action</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-border">
                        {phoneNumbers.map(num => (
                          <tr key={num.number_id} className="hover:bg-muted/30">
                            <td className="px-3 py-2 font-mono text-xs">{num.phone_number}</td>
                            <td className="px-3 py-2 text-xs text-muted-foreground truncate max-w-[180px]">{num.business_name || num.user_email}</td>
                            <td className="px-3 py-2 text-xs">{num.label || "-"}</td>
                            <td className="px-3 py-2 text-center">
                              {num.a2p_linked ? (
                                <Badge variant="outline" className="bg-emerald-500/15 text-emerald-600 dark:text-emerald-400 border-emerald-500/20 text-[10px]">
                                  <CheckCircle className="w-3 h-3 mr-1" />Linked
                                </Badge>
                              ) : (
                                <Badge variant="outline" className="bg-amber-500/15 text-amber-600 dark:text-amber-400 border-amber-500/20 text-[10px]">
                                  <AlertTriangle className="w-3 h-3 mr-1" />Not Linked
                                </Badge>
                              )}
                            </td>
                            <td className="px-3 py-2 text-right">
                              {!num.a2p_linked && (
                                <Button variant="outline" size="sm" className="h-7 text-xs" onClick={() => linkSingleNumber(num.number_id)} disabled={linkingId === num.number_id} data-testid={`link-number-${num.number_id}`}>
                                  {linkingId === num.number_id ? <RefreshCw className="w-3 h-3 animate-spin" /> : "Link to A2P"}
                                </Button>
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default AdminSystem;
